import React, { useEffect, useMemo, useState } from 'react'
import { Box, Stack } from '@mui/material'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { useLocation } from 'react-router-dom'
import type { OrgMemberRow } from 'pure-js/types/OrganizationMembership'
import type { OrgRole } from 'pure-js/types/OrganizationMembership'
import type { OrgInvitation } from 'pure-js/types/OrgInvitation'
import { OrgInvitationStatus } from 'pure-js/types/OrgInvitation'
import { FacilitatorRoutes } from '../../../../config/routes/routeEnums'

import { useOrganizationDetail, useOrgMembers, useOrgInvites } from '../../../../apiLayer/queryHooks/queries'
import {
  useRemoveUserFromOrganization,
  useUpdateOrgMember,
  useRevokeOrgInvite
} from '../../../../apiLayer/queryHooks/mutations'
import toast from 'react-hot-toast'
import { Button, Input, SegmentTabBar } from '../../../../interaxDesignSystemComponents'
import { PageWrapper } from '../../../../shared/components'
import { useRequestGoalSetting } from '../../../../apiLayer/queryHooks/coach'
import { RequestGoalsConfirmModal } from '../OrgMembersPage/RequestGoalsConfirmModal'
import { PageHeader } from '../../../../shared/components/PageHeader'
import { INTERAX_COLORS } from '../../../../constants/colors'
import { useTranslation } from '../../../../shared/hooks/useIntractionTranslate'
import { useDebouncedValue } from '../../../../shared/hooks/useDebouncedValue'
import { useClientPagination } from '../../../../shared/hooks/useClientPagination'
import { useOrgTenant } from '../../OrgContext'
import useAppState from '../../../../hooks/useAppState'
import { OrgInviteModal } from '../../components/OrgInviteModal'
import {
  filterMembersByQuery,
  countInvitesByStatus,
  filterInvitesByEmail,
  sortInvitesByRecency
} from '../../helpers/orgFilters'
import { OrgMembersTable } from '../OrgMembersPage/OrgMembersTable'
import { ChangeRoleModal } from '../OrgMembersPage/ChangeRoleModal'
import { RemoveMemberConfirmModal } from '../OrgMembersPage/RemoveMemberConfirmModal'
import { LeaderScopeModal } from '../../components/LeaderScopeModal/LeaderScopeModal'
import { OrgInvitesStatusTabs } from '../OrgInvitesPage/OrgInvitesStatusTabs'
import { OrgInvitesTable } from '../OrgInvitesPage/OrgInvitesTable'
import { RevokeInviteConfirmModal } from '../OrgInvitesPage/RevokeInviteConfirmModal'

const MEMBERS_PAGE_SIZE = 20 // members per page

enum OrgTeamPanel {
  MEMBERS = 'members',
  INVITATIONS = 'invitations'
}

function resolveInitialPanel(pathname: string, search: string): OrgTeamPanel {
  if (pathname.includes(FacilitatorRoutes.ORGANIZATION_INVITES)) return OrgTeamPanel.INVITATIONS
  if (new URLSearchParams(search).get('view') === 'invites') return OrgTeamPanel.INVITATIONS
  return OrgTeamPanel.MEMBERS
}

/**
 * Combined organization team page with top-level Members / Invitations tabs.
 */
export const OrgTeamPage: React.FC = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const { state } = useAppState()
  const currentUserId = state.user?.id
  const { activeMembership, isOrgAdmin, isPlatformSuper, canInviteMembers, canManageInvites } = useOrgTenant()
  const organizationId = activeMembership?.organizationId

  const [activePanel, setActivePanel] = useState<OrgTeamPanel>(() =>
    resolveInitialPanel(location.pathname, location.search)
  )

  const { data: membersRes, isLoading: membersLoading, isError: membersError } = useOrgMembers(organizationId)
  const { data: invitesRes, isLoading: invitesLoading, isError: invitesError } = useOrgInvites(organizationId)
  const { data: orgRes } = useOrganizationDetail(organizationId)
  const org = orgRes?.data
  const members: OrgMemberRow[] = useMemo(() => membersRes?.data ?? [], [membersRes])
  const invites: OrgInvitation[] = useMemo(() => invitesRes?.data ?? [], [invitesRes])

  const updateMember = useUpdateOrgMember()
  const removeMember = useRemoveUserFromOrganization()
  const revoke = useRevokeOrgInvite()

  const [membersSearchInput, setMembersSearchInput] = useState('')
  const membersDebouncedQuery = useDebouncedValue(membersSearchInput)

  const [inviteTab, setInviteTab] = useState<OrgInvitationStatus>(OrgInvitationStatus.PENDING)
  const [invitesSearchInput, setInvitesSearchInput] = useState('')
  const invitesDebouncedQuery = useDebouncedValue(invitesSearchInput)

  const [isInviteOpen, setIsInviteOpen] = useState(false)
  const [roleChangeTarget, setRoleChangeTarget] = useState<OrgMemberRow | null>(null)
  const [removeTarget, setRemoveTarget] = useState<OrgMemberRow | null>(null)
  const [goalsTarget, setGoalsTarget] = useState<OrgMemberRow | null>(null)
  const [leaderScopeTarget, setLeaderScopeTarget] = useState<OrgMemberRow | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<OrgInvitation | null>(null)

  const inviteCounts = useMemo(() => countInvitesByStatus(invites), [invites])
  const pendingInviteCount = inviteCounts[OrgInvitationStatus.PENDING] ?? 0
  const memberCount = members.length
  const maxEmployees = org?.limits?.maxEmployees
  const isLeaderRoleOffered = org?.leaderFeaturesEnabled === true

  const filteredMembers = useMemo(
    () => filterMembersByQuery(members, membersDebouncedQuery),
    [members, membersDebouncedQuery]
  )

  const filteredInvites = useMemo(() => {
    const byStatus = invites.filter((invite) => invite.status === inviteTab)
    return sortInvitesByRecency(filterInvitesByEmail(byStatus, invitesDebouncedQuery))
  }, [invites, inviteTab, invitesDebouncedQuery])

  const {
    page: membersPage,
    rowsPerPage: membersRowsPerPage,
    paginated: paginatedMembers,
    setPage: setMembersPage,
    setRowsPerPage: setMembersRowsPerPage,
    resetPage: resetMembersPage
  } = useClientPagination(filteredMembers, { defaultRowsPerPage: MEMBERS_PAGE_SIZE })

  const {
    page: invitesPage,
    rowsPerPage: invitesRowsPerPage,
    paginated: paginatedInvites,
    setPage: setInvitesPage,
    setRowsPerPage: setInvitesRowsPerPage,
    resetPage: resetInvitesPage
  } = useClientPagination(filteredInvites)

  useEffect(() => {
    setActivePanel(resolveInitialPanel(location.pathname, location.search))
  }, [location.pathname, location.search])

  useEffect(() => {
    resetMembersPage()
  }, [membersDebouncedQuery, resetMembersPage])

  useEffect(() => {
    resetInvitesPage()
  }, [inviteTab, invitesDebouncedQuery, resetInvitesPage])

  const closeRoleChange = () => setRoleChangeTarget(null)
  const confirmRoleChange = (newRole: OrgRole) => {
    if (!organizationId || !roleChangeTarget) return
    if (newRole === roleChangeTarget.role) {
      closeRoleChange()
      return
    }
    updateMember.mutate(
      { organizationId, userId: roleChangeTarget.userId, patch: { role: newRole } },
      { onSettled: closeRoleChange }
    )
  }

  const closeRemove = () => setRemoveTarget(null)
  const confirmRemove = () => {
    if (!organizationId || !removeTarget) return
    removeMember.mutate({ organizationId, userId: removeTarget.userId }, { onSettled: closeRemove })
  }

  const requestGoals = useRequestGoalSetting()
  const confirmRequestGoals = async () => {
    if (!goalsTarget) return
    try {
      await requestGoals.mutateAsync({ userId: goalsTarget.userId })
      toast.success(t('org.members.requestGoalsDone'))
    } catch {
      toast.error(t('org.members.requestGoalsError'))
    } finally {
      setGoalsTarget(null)
    }
  }

  const closeRevoke = () => setRevokeTarget(null)
  const confirmRevoke = () => {
    if (!organizationId || !revokeTarget) return
    revoke.mutate({ organizationId, inviteId: revokeTarget.id }, { onSettled: closeRevoke })
  }

  const hasNoMembers = !membersLoading && !membersError && members.length === 0
  const hasNoMemberMatches = !membersLoading && !membersError && members.length > 0 && filteredMembers.length === 0
  const hasNoInvites = !invitesLoading && !invitesError && invites.length === 0
  const hasNoInviteMatches = !invitesLoading && !invitesError && invites.length > 0 && filteredInvites.length === 0

  const teamTabs = useMemo(
    () => [
      { value: OrgTeamPanel.MEMBERS, label: t('org.members.title'), badge: memberCount },
      {
        value: OrgTeamPanel.INVITATIONS,
        label: t('org.invites.title'),
        badge: pendingInviteCount > 0 ? pendingInviteCount : invites.length
      }
    ],
    [t, memberCount, pendingInviteCount, invites.length]
  )

  // Coach goals can only be set for a member when the org runs the coach and the viewer may manage people.
  const canSetMemberGoals = org?.isAiCoachOn === true && canInviteMembers

  return (
    <PageWrapper>
      <Stack spacing={{ xs: 2, sm: 2.5, md: 3 }} sx={{ maxWidth: 1080, mx: 'auto', width: '100%' }}>
        <PageHeader
          title={t('org.team.title')}
          subtitle={t('org.team.subtitle', {
            memberCount,
            pendingCount: pendingInviteCount
          })}
          actions={
            canInviteMembers ? (
              <Button variant="primary" onClick={() => setIsInviteOpen(true)} data-testid="org-team-invite-button">
                {t('org.members.inviteButton')}
              </Button>
            ) : undefined
          }
        />

        <Box data-testid="org-team-panel-tabs">
          <SegmentTabBar
            variant="pill"
            ariaLabel={t('org.team.tabBarAria')}
            value={activePanel}
            onChange={(next) => setActivePanel(next as OrgTeamPanel)}
            tabs={teamTabs}
          />
        </Box>

        {activePanel === OrgTeamPanel.MEMBERS && (
          <Stack spacing={{ xs: 1.5, sm: 2, md: 2 }} role="tabpanel" id="org-team-panel-members">
            <Box sx={{ maxWidth: 420 }}>
              <Input
                value={membersSearchInput}
                onChange={(event) => setMembersSearchInput(event.target.value)}
                placeholder={t('org.members.searchPlaceholder')}
                type="search"
                fullWidth
                startIcon={<MagnifyingGlassIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.white70 }} />}
                data-testid="org-members-search"
              />
            </Box>

            <OrgMembersTable
              rows={paginatedMembers}
              totalFiltered={filteredMembers.length}
              isOrgAdmin={isOrgAdmin}
              currentUserId={currentUserId}
              onChangeRole={setRoleChangeTarget}
              onRemove={setRemoveTarget}
              onRequestGoals={canSetMemberGoals ? setGoalsTarget : undefined}
              onManagePeople={isOrgAdmin ? setLeaderScopeTarget : undefined}
              isLoading={membersLoading}
              isError={membersError}
              hasNoMembers={hasNoMembers}
              hasNoMatches={hasNoMemberMatches}
              page={membersPage}
              rowsPerPage={membersRowsPerPage}
              onPageChange={setMembersPage}
              onRowsPerPageChange={setMembersRowsPerPage}
            />
          </Stack>
        )}

        {activePanel === OrgTeamPanel.INVITATIONS && (
          <Stack spacing={{ xs: 1.5, sm: 2, md: 2 }} role="tabpanel" id="org-team-panel-invitations">
            <OrgInvitesStatusTabs value={inviteTab} onChange={setInviteTab} counts={inviteCounts} />

            <Box sx={{ maxWidth: 420 }}>
              <Input
                value={invitesSearchInput}
                onChange={(event) => setInvitesSearchInput(event.target.value)}
                placeholder={t('org.invites.searchPlaceholder')}
                type="search"
                fullWidth
                startIcon={<MagnifyingGlassIcon style={{ width: 18, height: 18, color: INTERAX_COLORS.white70 }} />}
                data-testid="org-invites-search"
              />
            </Box>

            <OrgInvitesTable
              rows={paginatedInvites}
              totalFiltered={filteredInvites.length}
              isOrgAdmin={canManageInvites}
              currentUserId={currentUserId}
              onRevoke={setRevokeTarget}
              isLoading={invitesLoading}
              isError={invitesError}
              hasNoInvites={hasNoInvites}
              hasNoMatches={hasNoInviteMatches}
              page={invitesPage}
              rowsPerPage={invitesRowsPerPage}
              onPageChange={setInvitesPage}
              onRowsPerPageChange={setInvitesRowsPerPage}
            />
          </Stack>
        )}

        <ChangeRoleModal
          target={roleChangeTarget}
          onClose={closeRoleChange}
          onConfirm={confirmRoleChange}
          isPending={updateMember.isPending}
          isLeaderRoleOffered={isLeaderRoleOffered}
        />

        <RemoveMemberConfirmModal
          target={removeTarget}
          onClose={closeRemove}
          onConfirm={confirmRemove}
          isPending={removeMember.isPending}
        />

        <RevokeInviteConfirmModal
          invite={revokeTarget}
          onClose={closeRevoke}
          onConfirm={confirmRevoke}
          isPending={revoke.isPending}
        />

        <LeaderScopeModal
          target={leaderScopeTarget}
          organizationId={organizationId ?? ''}
          members={members}
          onClose={() => setLeaderScopeTarget(null)}
        />

        <RequestGoalsConfirmModal
          target={goalsTarget}
          onClose={() => setGoalsTarget(null)}
          onConfirm={confirmRequestGoals}
          isPending={requestGoals.isPending}
        />

        <OrgInviteModal
          isOpen={isInviteOpen}
          onClose={() => setIsInviteOpen(false)}
          organizationId={organizationId}
          canSelectRole={isOrgAdmin || isPlatformSuper}
          isLeaderRoleOffered={isLeaderRoleOffered}
          memberCount={memberCount}
          maxEmployees={maxEmployees}
          pendingInviteCount={pendingInviteCount}
        />
      </Stack>
    </PageWrapper>
  )
}
